import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import Modal from '../../components/ui/Modal';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconBuilding,
  IconPlus,
  IconCheck,
  IconArrowRight,
} from '@tabler/icons-react';

export default function Sites() {
  const { t } = useTranslation();
  const { orgId, userProfile } = useAuth();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [siteName, setSiteName] = useState('');
  const [siteAddress, setSiteAddress] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const snap = await getDocs(collection(db, 'organisations', orgId, 'sites'));
        setSites(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const handleAdd = async () => {
    if (!siteName.trim()) { toast.error(t('set.err_site_name')); return; }
    setSaving(true);
    try {
      const newSite = {
        name: siteName.trim(),
        address: siteAddress.trim() || null,
        createdAt: serverTimestamp(),
        createdBy: userProfile?.id,
      };
      const ref = await addDoc(collection(db, 'organisations', orgId, 'sites'), newSite);
      setSites(prev => [...prev, { id: ref.id, ...newSite }]);
      setShowAddModal(false);
      setSiteName(''); setSiteAddress('');
      toast.success(t('set.site_added'));
    } catch (err) {
      console.error(err);
      toast.error(t('set.err_site_add'));
    }
    setSaving(false);
  };

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 680, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <IconBuilding size={20} color="var(--color-text-secondary)" stroke={1.5} />
            <h1 style={{ fontSize: 22, fontWeight: 400 }}>{t('dashboard.sites')}</h1>
          </div>
          <Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => setShowAddModal(true)}>
            {t('dashboard.add_site')}
          </Button>
        </div>

        {loading ? <LoadingSkeleton rows={3} card /> : sites.length === 0 ? (
          <Card>
            <EmptyState
              icon={<IconBuilding size={32} stroke={1.5} />}
              title={t('dashboard.no_sites')}
              message={t('set.no_sites_msg')}
              action={<Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => setShowAddModal(true)}>{t('dashboard.add_site')}</Button>}
            />
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sites.map(site => (
              <Card key={site.id} onClick={() => navigate('/settings/sites/' + site.id)} hover>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--color-brand-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <IconBuilding size={18} color="var(--color-brand)" stroke={1.5} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)' }}>{site.name}</div>
                    {site.address && <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{site.address}</div>}
                  </div>
                  <IconArrowRight size={15} color="var(--color-text-tertiary)" stroke={1.5} />
                </div>
              </Card>
            ))}
          </div>
        )}

        <Modal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          title={t('dashboard.add_site')}
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setShowAddModal(false)}>{t('common.cancel')}</Button>
              <Button variant="primary" onClick={handleAdd} disabled={saving} loading={saving} icon={<IconCheck size={14} stroke={1.5} />}>{t('dashboard.add_site')}</Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>{t('set.site_name')} <span style={{ color: 'var(--color-danger)' }}>*</span></label>
              <input type="text" placeholder={t('set.site_name_placeholder')} value={siteName} onChange={e => setSiteName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} autoFocus />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>{t('set.address')}</label>
              <input type="text" placeholder={t('set.address_placeholder')} value={siteAddress} onChange={e => setSiteAddress(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
}